import React, { memo, useCallback, useState } from 'react';

import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { AiProcessStepExecutionStatus } from 'constants/aiProcessRuns';
import useNowTick from 'hooks/useNowTick';

import { AiActivityTestIds } from '../../AiActivity.testIds';
import { formatDuration } from '../../AiActivity.utils';
import RunFailureForensics from '../../RunFailureForensics';
import StatusPill from '../../StatusPill';
import { StepViewModeEnum } from '../ExecutionDetailDrawer.constants';
import { emptyStateStyles } from '../ExecutionDetailDrawer.styles';

import StepConfirmationActions from './StepConfirmationActions';
import {
  detailsStyles,
  getTabStyles,
  headerDurationStyles,
  headerRowStyles,
  paneStyles,
  tabsRowStyles,
} from './StepDetailPane.styles';
import StepQuestionActions from './StepQuestionActions';
import StepRawArtifacts from './StepRawArtifacts';
import StepToolCalls from './StepToolCalls';
import StepTranscript from './StepTranscript';

const StepDetailPane = ({ step, execution, retryFromHereDependentCount }) => {
  // The pane is keyed by step id upstream, so it remounts per step — meaning a
  // failed step opens straight on Raw (where the real error blob lives) instead
  // of the Tool calls tab that has nothing to show for a failure.
  const isFailed = step?.status === AiProcessStepExecutionStatus.FAILED;
  const [viewMode, setViewMode] = useState(() =>
    isFailed ? StepViewModeEnum.RAW : StepViewModeEnum.TOOL_CALLS,
  );

  const onTabClick = useCallback(value => () => setViewMode(value), []);

  useNowTick(step?.status === AiProcessStepExecutionStatus.RUNNING);

  if (!step) {
    return (
      <Box data-testid={AiActivityTestIds.STEP_DETAIL_PANE} sx={emptyStateStyles}>
        <Typography variant="regular14">Select a step to see its details.</Typography>
      </Box>
    );
  }

  const isAwaitingConfirmation = step.status === AiProcessStepExecutionStatus.AWAITING_CONFIRMATION;
  const isAwaitingUserInput = step.status === AiProcessStepExecutionStatus.AWAITING_USER_INPUT;

  return (
    <Box data-testid={AiActivityTestIds.STEP_DETAIL_PANE} sx={paneStyles}>
      <Box sx={headerRowStyles}>
        <Typography variant="bold14">{step.name}</Typography>
        <StatusPill status={step.status} />
        <Typography sx={headerDurationStyles} variant="regular12">
          {formatDuration(step)}
        </Typography>
      </Box>
      <Box sx={detailsStyles}>
        {isFailed && (
          <RunFailureForensics
            canRetry
            errorMessage={step.errorMessage ?? execution?.errorMessage}
            errorReason={step.errorReason}
            recentEvents={execution?.recentEvents}
            recurringFailureCount={execution?.recurringFailureCount}
            retryFromHereDependentCount={retryFromHereDependentCount}
            runId={execution?.id}
            stepExecutionId={step.id}
          />
        )}
        {isAwaitingConfirmation && <StepConfirmationActions stepExecutionId={step.id} />}
        {isAwaitingUserInput && <StepQuestionActions stepExecutionId={step.id} />}
        <Box sx={tabsRowStyles}>
          <Box
            data-testid={AiActivityTestIds.STEP_TOOL_CALLS_TAB}
            sx={getTabStyles(viewMode === StepViewModeEnum.TOOL_CALLS)}
            onClick={onTabClick(StepViewModeEnum.TOOL_CALLS)}
          >
            <Typography variant="bold12">Tool calls</Typography>
          </Box>
          <Box
            sx={getTabStyles(viewMode === StepViewModeEnum.TRANSCRIPT)}
            onClick={onTabClick(StepViewModeEnum.TRANSCRIPT)}
          >
            <Typography variant="bold12">Transcript</Typography>
          </Box>
          <Box
            sx={getTabStyles(viewMode === StepViewModeEnum.RAW)}
            onClick={onTabClick(StepViewModeEnum.RAW)}
          >
            <Typography variant="bold12">Raw</Typography>
          </Box>
        </Box>
        {viewMode === StepViewModeEnum.TOOL_CALLS && <StepToolCalls stepExecutionId={step.id} />}
        {viewMode === StepViewModeEnum.TRANSCRIPT && <StepTranscript stepExecutionId={step.id} />}
        {viewMode === StepViewModeEnum.RAW && (
          <StepRawArtifacts isFailed={isFailed} stepExecutionId={step.id} />
        )}
      </Box>
    </Box>
  );
};

StepDetailPane.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
    startedAt: PropTypes.string,
    completedAt: PropTypes.string,
    errorMessage: PropTypes.string,
    errorReason: PropTypes.string,
  }),
  execution: PropTypes.shape({
    id: PropTypes.string,
    errorMessage: PropTypes.string,
    recentEvents: PropTypes.array,
    recurringFailureCount: PropTypes.number,
  }),
  retryFromHereDependentCount: PropTypes.number,
};

export default memo(StepDetailPane);
